import React, { useState, useEffect, useRef } from 'react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Leaderboard from './pages/Leaderboard';
import Tasks from './pages/Tasks';
import Admin from './pages/Admin';
import Notifications from './pages/Notifications';
import Production from './pages/Production';
import ChatBot from './components/ChatBot';
import SizeRatioView from './pages/SizeRatio';
import ErrorBoundary from './components/ErrorBoundary';

const API = '/api';

// ── ERP embed mode ─────────────────────────────────────────────────────────
// When the ERP shell (node-app.php) routes a form's NodeRouter here, it opens
// this app in an iframe with ?sso=<HMAC token>&form=<formId>. We silently
// exchange the SSO token for a session and render just that form's module,
// chrome-free (the ERP provides the surrounding navigation).
const _q = new URLSearchParams(window.location.search);
const SSO_TOKEN = _q.get('sso');
// Form id may arrive as ?form=5401 OR in the path the ERP shell iframes, e.g.
// mfgen.mindforgeerp.com/sizeratio/5401?sso=… (like BlinkBox's /blinkbox/6949).
const _pathForm = (window.location.pathname.match(/\/(\d{3,})(?:\/)?$/) || [])[1] || null;
const EMBED_FORM = _q.get('form') || _pathForm;    // e.g. '5401'
const EMBED = _q.get('embed') === '1' || !!EMBED_FORM || !!SSO_TOKEN;
const FORM_VIEW = { '5401': 'size-ratio' };        // ERP form id → embedded module
const EMBED_VIEW = FORM_VIEW[EMBED_FORM] || (_q.get('view') || null);

// Theme — light is the default; 'dj_theme' in localStorage persists the choice.
// Applied at module load so the login screen is themed before React renders.
document.documentElement.setAttribute('data-theme', localStorage.getItem('dj_theme') || 'light');
export function toggleTheme() {
  const next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', next);
  localStorage.setItem('dj_theme', next);
  return next;
}

// ── Web Push helpers ──
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}
export function pushSupported() {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}
// Returns 'on' if subscribed, throws with a friendly message otherwise.
export async function enablePush() {
  if (!pushSupported()) throw new Error('This browser does not support push notifications.');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notifications were blocked. Allow them in browser settings, then retry.');
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const { publicKey } = await api('/push/key');
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
  }
  await api('/push/subscribe', { method: 'POST', body: JSON.stringify({ subscription: sub }) });
  return 'on';
}
// Auto-enable push on login — silent, no UI. If permission is already granted we
// just (re)subscribe (so it stays on across restarts with zero taps). If it's still
// undecided we prompt once automatically. Never throws; failures fall back to the
// visible "Enable push" button. iOS-in-Safari-tab can't prompt — needs Home Screen.
export async function autoEnablePush() {
  try {
    if (!pushSupported()) return;
    if (Notification.permission === 'denied') return;
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return;
    }
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    const { publicKey } = await api('/push/key');
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
    }
    await api('/push/subscribe', { method: 'POST', body: JSON.stringify({ subscription: sub }) });
  } catch (e) { /* silent — user can still use the Enable push button */ }
}
export async function pushStatus() {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && await reg.pushManager.getSubscription();
    return sub ? 'on' : 'off';
  } catch { return 'off'; }
}

// Fire-and-forget client-side activity tracker. Used for events the backend
// can't observe on its own — page views, order opens, searches, etc.
// Never blocks the UI; failures are swallowed.
export function track(action, opts = {}) {
  try {
    const token = localStorage.getItem('dj_token');
    if (!token) return;
    fetch(API + '/activity/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, ...opts })
    }).catch(() => {});
  } catch {}
}

export function api(path, opts = {}) {
  const token = localStorage.getItem('dj_token');
  return fetch(API + path, {
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...opts
  }).then(async r => {
    // Token expired or rejected → wipe session and bounce to Login.
    // This prevents the "blank screen / can't login" state caused by stale tokens.
    if (r.status === 401 || r.status === 403) {
      try {
        localStorage.removeItem('dj_token');
        localStorage.removeItem('dj_user');
      } catch {}
      // Soft reload — React will mount Login screen.
      if (!window.__authReloading) {
        window.__authReloading = true;
        window.location.reload();
      }
      return {};
    }
    return r.json();
  });
}

export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dj_user')); } catch { return null; }
  });
  // Embedded in the ERP shell: exchange the ?sso= token for a session on load.
  // Raw fetch (not api()) so a 401 doesn't trigger api()'s reload-to-Login loop.
  const [ssoBusy, setSsoBusy] = useState(!!SSO_TOKEN);
  useEffect(() => {
    if (!SSO_TOKEN) return;
    fetch(API + '/auth/sso', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: SSO_TOKEN }) })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(r => {
        if (r && r.token) {
          try { localStorage.setItem('dj_token', r.token); localStorage.setItem('dj_user', JSON.stringify(r.user)); } catch {}
          setUser(r.user);
        }
      })
      .catch(() => {})
      .finally(() => setSsoBusy(false));
  }, []);
  const [tab, setTab] = useState('dashboard');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderFilter, setOrderFilter] = useState(null); // {buyer, type:'garment'|'fabric'} or null
  const [dashboardView, setDashboardView] = useState('home'); // 'home' | 'tas' | 'gsp' — persists across tab switches
  const [toast, setToast] = useState(null);
  const [unreadTasks, setUnreadTasks] = useState(0);
  const [unreadNotif, setUnreadNotif] = useState(0);

  // Tasks + ChatBot are restricted to management users only (Ritesh, Deepali).
  // is_management = 1 is set on those rows in dj_dev_users.
  // Extra safety: also accept exact display-name match in case schema changes.
  const isPrivileged = !!user && (
    user.isManagement === true ||
    ['Ritesh', 'Deepali'].includes((user.name || '').trim())
  );

  // Admin (activity log + error log) is a SEPARATE privilege.
  // Granted to Ritesh, Deepali, Raj via dj_dev_users.is_admin = 1.
  // Decoupled from isManagement so Raj can see logs without unlocking
  // Tasks/ChatBot. Name-match fallback covers stale tokens / schema drift.
  const isAdmin = !!user && (
    user.isAdmin === true ||
    ['Ritesh', 'Deepali', 'Raj'].includes((user.name || '').trim())
  );

  // Demo account (MindForge) — all data is fake sample data. Show a badge.
  const isDemo = !!user && (user.isDemo === true || (user.name || '').trim() === 'MindForge');

  // Keep screen rights current without a re-login: re-read them on open, when the
  // window regains focus, and every 5 minutes. Raw fetch, so a failed call never
  // bounces the user to Login; the stored rights only change when the list does.
  useEffect(() => {
    if (!user || user.isManagement) return;
    let stop = false;
    function refresh() {
      const token = localStorage.getItem('dj_token');
      if (!token) return;
      fetch(API + '/auth/my-rights', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          if (stop || !d || !Array.isArray(d.rights)) return;
          setUser(u => {
            if (!u || JSON.stringify(u.rights || []) === JSON.stringify(d.rights)) return u;
            const next = { ...u, rights: d.rights };
            try { localStorage.setItem('dj_user', JSON.stringify(next)); } catch {}
            return next;
          });
        })
        .catch(() => {});
    }
    refresh();
    const id = setInterval(refresh, 5 * 60 * 1000);
    window.addEventListener('focus', refresh);
    return () => { stop = true; clearInterval(id); window.removeEventListener('focus', refresh); };
  }, [user?.name]);

  // Poll unread task count every 30s — only for privileged users
  useEffect(() => {
    if (!user || !isPrivileged) return;
    let stop = false;
    function tick() {
      api('/tasks/unread-count').then(d => { if (!stop) setUnreadTasks(d?.unread || 0); }).catch(() => {});
    }
    tick();
    const id = setInterval(tick, 30000);
    return () => { stop = true; clearInterval(id); };
  }, [user, tab, isPrivileged]);

  // Poll unread notification count (all logged-in users)
  useEffect(() => {
    if (!user) return;
    let stop = false;
    const tick = () => api('/notifications').then(d => { if (!stop) setUnreadNotif(d?.unread || 0); }).catch(() => {});
    tick();
    const id = setInterval(tick, 60000);
    return () => { stop = true; clearInterval(id); };
  }, [user, tab]);

  // Auto-enable push on login (silent). Returning users re-subscribe with no taps;
  // new users get the permission prompt once. Slight delay so it doesn't fire
  // during the login transition.
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => { autoEnablePush(); }, 2500);
    return () => clearTimeout(t);
  }, [user]);

  // Push-click routing: open the linked order/view when a push notification is
  // tapped — from the SW postMessage (app already open) or the ?notif= cold-open param.
  useEffect(() => {
    if (!user) return;
    const go = (link) => {
      if (!link) return;
      if (String(link).startsWith('view:')) { setDashboardViewWithHistory(link.slice(5)); setTab('dashboard'); }
      else openOrder({ tas_no: link });
    };
    const params = new URLSearchParams(window.location.search);
    const q = params.get('notif');
    if (q) { go(q); window.history.replaceState(null, '', window.location.pathname); }
    const onMsg = (e) => { if (e.data && e.data.notifLink) go(e.data.notifLink); };
    if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', onMsg);
    return () => { if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', onMsg); };
  }, [user]);


  // ── Browser back-button wiring ──
  // We push a marker history entry every time the user navigates deeper.
  // On popstate, we pop one level off our internal stack instead of leaving the app.
  const navStack = useRef([]); // each entry: () => void  (the "go back one level" function)
  const pushNav = (backFn) => {
    navStack.current.push(backFn);
    window.history.pushState({ djnav: navStack.current.length }, '');
  };

  function gotoOrdersForBuyer(buyer, type) {
    // Push a back-action that returns to the buyer drill-down on Dashboard
    pushNav(() => { setOrderFilter(null); setTab('dashboard'); });
    setDashboardView(type === 'garment' ? 'tas-orders' : 'gsp-orders');
    setOrderFilter({ buyer, type });
    setTab('orders');
    track('buyer.drilldown', { entityType: type, entityId: buyer, details: { buyer, type } });
  }

  function openOrder(o) {
    pushNav(() => setSelectedOrder(null));
    setSelectedOrder(o);
    track('order.view', {
      entityType: o?.order_type || (String(o?.tas_no || '').startsWith('GSP/') ? 'gsp' : 'tas'),
      entityId:   o?.tas_no || o?.order_no || null,
      details: { buyer: o?.buyer, factory: o?.factory, delay: o?.Delay_days }
    });
  }

  function backToBuyers() {
    setOrderFilter(null);
    setTab('dashboard');
    if (navStack.current.length) navStack.current.pop();
    // The pushState entry is still in browser history; consume it
    window.history.back();
  }

  // Wrap setDashboardView so going deeper pushes a back action
  function setDashboardViewWithHistory(next) {
    setDashboardView(prev => {
      const wasHome   = prev === 'home';
      const wasMenu   = prev === 'tas' || prev === 'gsp' || prev === 'rw' || prev === 'tas-daily' || prev === 'tas-production';
      const goingUp   = next === 'home' || next === 'rw' || (prev === 'tas-daily' && next === 'tas') || (prev === 'tas-production' && next === 'tas');
      const goingDeep = (wasHome && next !== 'home') || (wasMenu && !goingUp && next !== prev);
      if (goingDeep) {
        const restore = () => setDashboardView(prev);
        pushNav(restore);
      } else if (goingUp && navStack.current.length > 0) {
        navStack.current.pop();
      }
      return next;
    });
  }

  // popstate listener — fires on browser/mobile back button
  useEffect(() => {
    function onPop(e) {
      const fn = navStack.current.pop();
      if (fn) {
        fn();
      } else {
        setDashboardView(current => {
          if (current === 'tas-production') return 'tas';
          if (current === 'tas' || current === 'gsp' || current === 'tas-daily') return 'rw';
          if (current === 'rw') return 'home';
          return current;
        });
        // No internal nav left — re-push so the browser doesn't actually leave
        window.history.pushState({ djnav: 0 }, '');
      }
    }
    window.addEventListener('popstate', onPop);
    // Seed an initial history entry so the very first back doesn't exit the SPA
    window.history.pushState({ djnav: 0 }, '');
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function logout() {
    // Track BEFORE clearing the token (track() needs the token to authenticate)
    track('logout');
    localStorage.removeItem('dj_token');
    localStorage.removeItem('dj_user');
    setUser(null);
  }

  // Still exchanging the ERP SSO token — show a neutral splash, never the login form.
  if (ssoBusy) return <div className="loading" style={{ padding: 40, textAlign: 'center' }}>SIGNING IN…</div>;

  if (!user) return <Login onLogin={u => { setUser(u); }} />;

  // ── ERP embed: render only the requested module, no app chrome/nav ──
  if (EMBED && EMBED_VIEW === 'size-ratio') {
    const EmbedHeader = () => null;   // the module supplies its own title bars
    return (
      <>
        {toast && <div className="xp-toast">{toast}</div>}
        <div className="screen">
          <SizeRatioView Header={EmbedHeader} setView={() => {}} embedded />
        </div>
      </>
    );
  }

  // Factory role — one and only screen. War Room, Alerts, Reports, Tasks,
  // ChatBot and the bottom nav all suppressed. The Production screen owns
  // its own header + logout button.
  if (user.isFactory === true) {
    return <Production user={user} onLogout={logout} />;
  }

  // Fixed corner badge shown on every screen for the demo account.
  const demoBadge = isDemo ? (
    <div style={{
      position: 'fixed', top: 0, right: 0, zIndex: 9999,
      background: 'var(--gold, #D4B978)', color: 'var(--on-gold)',
      fontSize: 9, fontWeight: 800, letterSpacing: 1.5,
      padding: '3px 10px', borderBottomLeftRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.4)', pointerEvents: 'none'
    }}>DEMO · SAMPLE DATA</div>
  ) : null;

  if (selectedOrder) {
    return (
      <>
        {demoBadge}
        {toast && <div className="xp-toast">{toast}</div>}
        <ErrorBoundary
          title="Could not load order details"
          onReset={() => setSelectedOrder(null)}
          onHome={() => setSelectedOrder(null)}
        >
          <OrderDetail
            order={selectedOrder}
            onBack={() => { window.history.back(); }}
            onXP={showToast}
          />
        </ErrorBoundary>
      </>
    );
  }

  return (
    <>
      {demoBadge}
      {toast && <div className="xp-toast">{toast}</div>}

      <div className="screen">
        <ErrorBoundary
          title="Something went wrong loading this screen"
          onReset={() => { setDashboardView('home'); setTab('dashboard'); }}
          onHome={() => { setDashboardView('home'); setTab('dashboard'); }}
        >
          {tab === 'dashboard' && <Dashboard user={user} onLogout={logout} onOrderClick={openOrder} onBuyerClick={gotoOrdersForBuyer} view={dashboardView} setView={setDashboardViewWithHistory} pushBack={pushNav} />}
          {tab === 'orders' && <Orders user={user} onOrderClick={openOrder} filter={orderFilter} clearFilter={() => setOrderFilter(null)} onBackToBuyers={backToBuyers} />}
          {tab === 'notifications' && <Notifications user={user} onOrderClick={openOrder}
            onOpenView={v => { setDashboardViewWithHistory(v); setTab('dashboard'); }} />}
          {tab === 'leaderboard' && <Leaderboard user={user} />}
          {tab === 'tasks' && isPrivileged && <Tasks user={user} />}
          {tab === 'admin' && isAdmin && <Admin user={user} />}
        </ErrorBoundary>
      </div>

      {isPrivileged && <ChatBot user={user} onOrderClick={openOrder} setTab={setTab} setView={setDashboardViewWithHistory} />}

      <nav className="bottom-nav">
        <div className={`nav-item ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => { setDashboardView('home'); setTab('dashboard'); track('page.view', { entityId: 'war-room' }); }}>
          <span className="nav-icon">⚡</span>
          <span>WAR ROOM</span>
        </div>
        <div className={`nav-item ${tab === 'notifications' ? 'active' : ''}`} onClick={() => { setTab('notifications'); track('page.view', { entityId: 'notifications' }); }} style={{ position: 'relative' }}>
          <span className="nav-icon">🔔</span>
          <span>ALERTS</span>
          {unreadNotif > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 'calc(50% - 18px)',
              background: 'var(--red)', color: '#fff', fontSize: 9, fontWeight: 700,
              minWidth: 16, height: 16, borderRadius: 8, padding: '0 4px',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
            }}>{unreadNotif > 9 ? '9+' : unreadNotif}</span>
          )}
        </div>
        {isPrivileged && (
          <div className={`nav-item ${tab === 'tasks' ? 'active' : ''}`} onClick={() => { setTab('tasks'); track('page.view', { entityId: 'tasks' }); }} style={{ position: 'relative' }}>
            <span className="nav-icon">✓</span>
            <span>TASKS</span>
            {unreadTasks > 0 && (
              <span style={{
                position: 'absolute', top: 4, right: 'calc(50% - 18px)',
                background: 'var(--red)', color: 'var(--text)', fontSize: 9, fontWeight: 700,
                minWidth: 16, height: 16, borderRadius: 8, padding: '0 4px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
              }}>{unreadTasks}</span>
            )}
          </div>
        )}
        <div className={`nav-item ${tab === 'leaderboard' ? 'active' : ''}`} onClick={() => { setTab('leaderboard'); track('page.view', { entityId: 'rankings' }); }}>
          <span className="nav-icon">🏆</span>
          <span>RANKINGS</span>
        </div>
        {isAdmin && (
          <div className={`nav-item ${tab === 'admin' ? 'active' : ''}`} onClick={() => { setTab('admin'); track('page.view', { entityId: 'admin' }); }}>
            <span className="nav-icon">🛡️</span>
            <span>ADMIN</span>
          </div>
        )}
      </nav>
    </>
  );
}
